import { BaseCard, useCardActions } from '@/components/ui/base-card'
import { useState } from 'react'

import { AddTargetedAreaModal } from '../AddTargetedAreaModal/AddTargetedAreaModal'

import type { County, Location, TargetingType } from 'honeygrid-types'
import type { FC } from 'react'

interface TargetedAreaCardProps {
	type: 'radius' | 'county' | 'zipcode'
	data: {
		locationId?: Location['uuid']
		radius?: number
		county?: County['id']
		zipCode?: string
	}
	onRemove: () => void
	onEdit: (targetedArea: {
		type: TargetingType
		data: {
			locationId?: Location['uuid']
			radius?: number
			county?: County['id']
			zipCode?: string
		}
	}) => void
	locations?: Location[]
	counties?: County[]
	zipCodes?: string[]
	totalTargetedAreas: number
}

export const TargetedAreaCard: FC<TargetedAreaCardProps> = ({
	type,
	data,
	onRemove,
	onEdit,
	locations,
	counties = [],
	zipCodes = [],
	totalTargetedAreas,
}) => {
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const { createEditAction, createRemoveAction } = useCardActions()

	const getHeadlineAndSubheadline = () => {
		if (type === 'radius') {
			const location = locations?.find((l) => l.uuid === data.locationId)
			const headline = `${data.radius} mile radius`
			const subheadline = location?.name ?? 'Unknown Location'
			return { headline, subheadline }
		} else if (type === 'county') {
			const county = counties?.find((c) => c.id === data.county)
			const headline = '1 County'
			const subheadline = county ? `${county.name}, ${county.state}` : 'Unknown County'
			return { headline, subheadline }
		} else {
			const headline = '1 Zip code'
			const subheadline = data.zipCode ?? 'Unknown Zip Code'
			return { headline, subheadline }
		}
	}

	const { headline, subheadline } = getHeadlineAndSubheadline()

	// Create actions for the card
	const actions = [createEditAction(() => setIsEditModalOpen(true)), createRemoveAction(onRemove)]

	return (
		<>
			<BaseCard title={headline} actions={actions} showActionsOnHover={true} className="min-w-0">
				<p className="text-sm text-muted-foreground truncate">{subheadline}</p>
			</BaseCard>
			<AddTargetedAreaModal
				open={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				onSave={(targetedArea) => {
					onEdit(targetedArea)
					setIsEditModalOpen(false)
				}}
				onRemove={totalTargetedAreas > 1 ? onRemove : undefined}
				locations={locations}
				counties={counties}
				zipCodes={zipCodes}
				initialData={{
					type: type === 'radius' ? 'radius' : type === 'county' ? 'county' : 'zip code',
					data,
				}}
			/>
		</>
	)
}
